<template>
  <div
    class="v3-file-upload ps__child--consume"
    :class="{
      'is-invalid': invalidFeedback
    }"
  >
    <div v-if="accessibleLabel" class="v3-form-label d-block mb-1">
      {{ accessibleLabel }}
    </div>
    <div class="v3-file-upload-input-container mb-1">
      <button
        type="button"
        class="custom-file-upload"
        :aria-label="uploadControlAccessibleName"
        :aria-describedby="uploadControlDescribedBy"
        @click="openFilePicker"
      >
        <div class="icon-file-upload">
          <i class="ri-file-2-line" aria-hidden="true" />
        </div>

        <div class="label-file-upload text-center">
          {{ DEFAULT_UPLOAD_CONTROL_TEXT }}
        </div>
      </button>
      <input
        :id="resolvedInputId"
        type="file"
        tabindex="-1"
        :aria-label="uploadControlAccessibleName"
        :aria-invalid="invalidFeedbackMessage ? 'true' : 'false'"
        :aria-describedby="uploadControlDescribedBy"
        @change="onChange"
      />
    </div>
    <div
      v-if="invalidFeedbackMessage"
      :id="invalidFeedbackId"
      class="invalid-message text-start"
      role="alert"
      aria-live="assertive"
    >
      {{ invalidFeedbackMessage }}
    </div>
    <a
      v-if="downloadExampleFilePath != ''"
      :href="downloadExampleFilePath"
      :download="downloadFileName"
      class="v3-file-upload-download text-decoration-none"
    >
      <i class="ri-download-2-line download-icon" aria-hidden="true" />
      Download Example
    </a>
    <div v-if="state.uploadedFileNames">
      <div
        v-for="(filename, index) in state.uploadedFileNames"
        :key="index"
        class="mt-2"
      >
        <hr />
        <b>{{ filename ? filename : "" }}</b>
        <button
          type="button"
          class="v3-file-upload-delete-icon btn btn-link p-0 border-0"
          :aria-label="`${$t('common.delete')} ${filename}`"
          @click="removeFile(filename)"
        >
          <i class="ri-delete-bin-line" aria-hidden="true" />
        </button>
        <hr />
      </div>
    </div>
    <HelperBox v-if="helpText != ''" :id="helperTextId">
      {{ helpText }}
    </HelperBox>
  </div>
</template>

<script>
import { computed, reactive, toRefs } from "vue"
import HelperBox from "../Alerts/HelperBox.vue"

const DEFAULT_UPLOAD_CONTROL_TEXT = "Select a file to upload"

const generateFileInputId = () =>
  `v3-file-upload-${Math.random().toString(36).slice(2, 11)}`

export default {
  components: { HelperBox },
  props: {
    invalidFeedback: [String, Array],
    uploadedFile: File,
    /** Associates visible label text with the file input (`for` / `id`). */
    inputId: {
      type: String,
      default: ""
    },
    accessibleLabel: {
      type: String,
      default: ""
    },
    downloadExampleFilePath: {
      type: String,
      default: ""
    },
    helpText: {
      type: String,
      default: ""
    },
    multipleFiles: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue", "remove-file"],
  setup(props, { emit }) {
    const fallbackInputId = generateFileInputId()
    const resolvedInputId = computed(() => props.inputId || fallbackInputId)
    const invalidFeedbackId = computed(
      () => `${resolvedInputId.value}-feedback`
    )
    const helperTextId = computed(() => `${resolvedInputId.value}-helper`)
    const uploadControlAccessibleName = computed(() => {
      const contextualLabel = props.accessibleLabel?.trim()
      if (!contextualLabel) {
        return DEFAULT_UPLOAD_CONTROL_TEXT
      }
      return `${DEFAULT_UPLOAD_CONTROL_TEXT}. ${contextualLabel}`
    })
    const uploadControlDescribedBy = computed(() => {
      const describedByIds = []
      if (invalidFeedbackMessage.value) {
        describedByIds.push(invalidFeedbackId.value)
      }
      if (props.helpText) {
        describedByIds.push(helperTextId.value)
      }
      return describedByIds.length ? describedByIds.join(" ") : undefined
    })
    const downloadFileName = computed(() => {
      const path = props.downloadExampleFilePath
      if (!path) return ""
      const parts = path.split("/")
      return parts[parts.length - 1] || "sample.csv"
    })
    const state = reactive({
      uploadedFileNames: []
    })
    const onChange = (event) => {
      const files = event.target.files
      if (!files?.length) {
        emit("update:modelValue", event)
        return
      }
      if (props.multipleFiles) {
        state.uploadedFileNames.push(files[0].name)
      } else {
        state.uploadedFileNames = [files[0].name]
      }
      emit("update:modelValue", event)
    }

    const invalidFeedbackMessage = computed(() => {
      return props.invalidFeedback && Array.isArray(props.invalidFeedback)
        ? props.invalidFeedback[0]
        : props.invalidFeedback
    })

    const removeFile = (filename) => {
      const indexOfFile = state.uploadedFileNames.indexOf(filename)
      state.uploadedFileNames.splice(indexOfFile, 1)
      emit("remove-file", filename)
    }

    const openFilePicker = () => {
      const input = document.getElementById(resolvedInputId.value)
      if (input && typeof input.click === "function") {
        input.click()
      }
    }

    return {
      ...toRefs(props),
      state,
      onChange,
      removeFile,
      openFilePicker,
      DEFAULT_UPLOAD_CONTROL_TEXT,
      invalidFeedbackMessage,
      invalidFeedbackId,
      helperTextId,
      uploadControlAccessibleName,
      uploadControlDescribedBy,
      downloadFileName,
      resolvedInputId
    }
  }
}
</script>

<style></style>
